import type { ReactNode } from "react";

/**
 * One header for every collapsible panel of Servis (audit S2-12, S2-13).
 *
 * Half of Servis introduced itself and half of it did not. The four sections
 * that are always open carry a kicker, a title and one sentence saying what
 * the panel is for; the seven collapsible ones carried a bare 13px heading in
 * `var(--amber)` - which is the exact background colour of every primary
 * button on the same screen, so the colour that means "press me here" was
 * being spent on static text. And five of the seven renamed themselves on
 * opening: the reader clicked "Nedávná aktivita" and landed on "Časová osa"
 * thirty pixels lower, clicked "Importovat dossier" and landed on "Import
 * dossieru". Two names for one thing is one name too many; nobody can build a
 * memory of a menu that changes under their hand.
 *
 * So the summary IS the panel's introduction, in the same anatomy the open
 * sections use, and the inner duplicate heading is gone. Amber goes back to
 * meaning "an action happens here".
 */
export function AdminPanel(props: {
  /** Matches the `data-admin-tool` the audits and guards select on. */
  tool: string;
  /**
   * F138d: the category is the GROUP's heading now, so a panel inside a group
   * does not repeat it. It stayed optional because a kicker that says
   * something the group cannot (a rehearsal state, a warning) is still worth
   * a line - and because dropping it silently everywhere would have been a
   * pattern change with no way back.
   */
  kicker?: string;
  title: string;
  /** One sentence: what this panel is for. The same sentence the panel used
   * to repeat inside itself. */
  lead: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details className="admin-tool-group" data-admin-tool={props.tool} open={props.defaultOpen}>
      {/* 🔴 A summary laid out as flex loses the browser's disclosure marker,
          and a summary whose only child is a block leaves the marker stranded
          on a line of its own above the header. So the caret is an explicit
          element, the same `.disclosure-caret` the service drawer uses. */}
      <summary>
        <span aria-hidden="true" className="disclosure-caret">
          <svg viewBox="0 0 24 24" focusable="false"><path d="m8 5 8 7-8 7" /></svg>
        </span>
        <span className="admin-panel-head">
          {props.kicker && <span className="panel-label">{props.kicker}</span>}
          <span className="admin-panel-title">{props.title}</span>
          <span className="admin-panel-lead">{props.lead}</span>
        </span>
      </summary>
      <div className="admin-panel-body">{props.children}</div>
    </details>
  );
}

/**
 * One group of Správa: a heading that names the question the tools under it
 * answer, and the panels themselves, all shut.
 *
 * The audit's finding this shape answers: thirteen tools in one drawer, four
 * of them always open and nine shut, and the difference said nothing about
 * importance - it was the order in which the waves happened to be built. One
 * rule now: the GROUP is open, its panels are closed, except the one the
 * operator arrived at through a link.
 */
export function AdminGroup(props: {
  /** Stable key the guards and the deep links select on. */
  group: "operations" | "clients" | "automations" | "people";
  title: string;
  lead: string;
  children: ReactNode;
}) {
  return (
    <section className="admin-group" data-admin-group={props.group}
      aria-labelledby={`admin-group-${props.group}-title`}>
      <div className="admin-group-head">
        <h2 className="admin-group-title" id={`admin-group-${props.group}-title`}>{props.title}</h2>
        <p className="admin-group-lead">{props.lead}</p>
      </div>
      <div className="admin-group-panels">{props.children}</div>
    </section>
  );
}
